import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { pushApi } from "@/api";
import type { PushBadge } from "@/api/push";
import { useAcceptedCount } from "@/staff/quotes/AcceptedCountContext";
import { useUnread } from "@/staff/letters/UnreadContext";
import { useChatFeed } from "@/staff/chat/ChatContext";
import {
  currentSubscription,
  disablePush,
  enablePush,
  pushSupported,
  setAppBadge,
  subscriptionFields,
} from "@/lib/push";

/**
 * Web Push state for the staff console (Safari Dock notifications).
 *
 * Two jobs:
 *   1. The enable/disable toggle in the user menu — subscription lives in the
 *      browser, the row lives in Xano (push/subscribe upserts by endpoint).
 *      On mount an already-granted browser silently re-syncs its row, so
 *      last_seen_at stays honest and a Safari-side resubscribe is re-captured.
 *   2. The Dock badge — polls push/badge (same 60s / pause-when-hidden shape
 *      as AcceptedCountContext) and mirrors it via navigator.setAppBadge, so
 *      the icon tracks reality while the app is open instead of freezing at
 *      the last push payload's count. Pushes carry the same number
 *      (staff_attention_count) when the app is closed.
 */

interface PushState {
  supported: boolean;
  enabled: boolean;
  busy: boolean;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
  /** Leads nav badge: actionable leads + THIS viewer's unseen bookings. */
  leadsBadge: number;
  unseenBookings: number;
  /** Stamp bookings as seen and re-sync the badges (Pipeline calls this). */
  markBookingsSeen: () => Promise<void>;
}

const PushCtx = createContext<PushState>({
  supported: false,
  enabled: false,
  busy: false,
  enable: async () => {},
  disable: async () => {},
  leadsBadge: 0,
  unseenBookings: 0,
  markBookingsSeen: async () => {},
});

const POLL_MS = 60_000;

export function PushProvider({ children }: { children: ReactNode }) {
  const supported = useMemo(() => pushSupported(), []);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  // Re-sync a pre-existing subscription on load (permission already granted).
  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    void (async () => {
      const sub = await currentSubscription();
      if (!sub || cancelled) return;
      setEnabled(true);
      try {
        await pushApi.subscribe({ ...subscriptionFields(sub), ua_label: navigator.userAgent });
      } catch {
        /* offline or expired token — the row refreshes on the next load */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [supported]);

  const enable = useCallback(async () => {
    setBusy(true);
    try {
      const sub = await enablePush();
      await pushApi.subscribe({ ...subscriptionFields(sub), ua_label: navigator.userAgent });
      setEnabled(true);
    } finally {
      setBusy(false);
    }
  }, []);

  const disable = useCallback(async () => {
    setBusy(true);
    try {
      const endpoint = await disablePush();
      if (endpoint) await pushApi.unsubscribe(endpoint).catch(() => {});
      setEnabled(false);
    } finally {
      setBusy(false);
    }
  }, []);

  // Badge mirror. Runs regardless of the toggle: the badge belongs to the
  // installed app, not to the push subscription. The Dock count is
  // per-person (shared attention + the viewer's unread letters + unread
  // chat); the response also carries the per-viewer unseen-bookings count
  // for the Leads nav badge. Keyed on the accepted count AND the letters
  // and chat unread totals so the icon reacts the same instant the nav
  // badges do — converting a quote, reading a letter, or opening a chat
  // must never leave the Dock showing a stale number until the next poll.
  const { count: acceptedCount } = useAcceptedCount();
  const { unread: lettersUnread } = useUnread();
  const { totalUnread: chatUnread } = useChatFeed();
  const [badgeInfo, setBadgeInfo] = useState<PushBadge | null>(null);
  const refreshBadge = useCallback(async () => {
    try {
      const res = await pushApi.badge();
      setBadgeInfo(res);
      await setAppBadge(res.count);
    } catch {
      /* keep the last badge rather than flicker */
    }
  }, []);

  const markBookingsSeen = useCallback(async () => {
    try {
      await pushApi.bookingsSeen();
      await refreshBadge();
    } catch {
      /* the badge just stays until the next successful mark */
    }
  }, [refreshBadge]);

  useEffect(() => {
    const refresh = refreshBadge;
    let interval: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (!interval) interval = setInterval(() => void refresh(), POLL_MS);
    };
    const stop = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        void refresh();
        start();
      }
    };
    void refresh();
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [acceptedCount, lettersUnread?.total, chatUnread, refreshBadge]);

  const value = useMemo<PushState>(
    () => ({
      supported,
      enabled,
      busy,
      enable,
      disable,
      leadsBadge: (badgeInfo?.leads_actionable ?? 0) + (badgeInfo?.unseen_bookings ?? 0),
      unseenBookings: badgeInfo?.unseen_bookings ?? 0,
      markBookingsSeen,
    }),
    [supported, enabled, busy, enable, disable, badgeInfo, markBookingsSeen],
  );
  return <PushCtx.Provider value={value}>{children}</PushCtx.Provider>;
}

export function usePush(): PushState {
  return useContext(PushCtx);
}
